import React, { useState } from 'react';
import { auth } from '../firebase-config';
import { createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, sendEmailVerification } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { notifyNewUser } from '../lib/adminNotify';
import Navbar from './Navbar';
import Footer from './Footer';
import { ThemeProvider, Button, Grid, TextField, Link as MuiLink, Typography } from '@mui/material';
import theme from '../theme';
import { Link, useNavigate } from 'react-router-dom';
import { useT } from '../i18n/LanguageContext';

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const t = useT();

  // Function to validate the password
  const validatePassword = (password) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasNumber = /\d/.test(password);

    return hasUpperCase && hasSpecialChar && hasNumber;
  };

  const signup = async () => {
    if (password !== confirmPassword) {
      setError(t('auth.passwordsMismatch'));
      return;
    }

    if (!validatePassword(password)) {
      setError(t('auth.passwordRequirements'));
      return;
    }

    try {
      // Create user with email and password
      setError('')
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send email verification
      await sendEmailVerification(user);

      // Initialize Firestore and assign the 'Free' role to the new user
      const db = getFirestore();
      await setDoc(doc(db, `users/${user.uid}`), {
        role: 'Free',
        email: user.email,
        displayName: user.displayName || 'N/A',
        createdAt: new Date(),
      });

      notifyNewUser(user.displayName || '');
      setSuccessMessage(t('auth.signupSuccess'));
    } catch (error) {
      console.error('Error during registration:', error.message);
      setError(error.message);
    }
  };

  // Function to handle Google Sign-In
  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Initialize Firestore and assign the 'Free' role to the new user (if not already in database)
      const db = getFirestore();
      const userDocRef = doc(db, `users/${user.uid}`);
      const existed = (await getDoc(userDocRef)).exists();
      await setDoc(userDocRef, {
        role: 'Free',
        email: user.email,
        displayName: user.displayName || 'N/A',
        createdAt: new Date(),
      }, { merge: true });

      if (!existed) notifyNewUser(user.displayName || '');
      setSuccessMessage(t('auth.signupSuccessGoogle'));
      navigate('/');
    } catch (error) {
      console.error('Error during Google Sign-In:', error.message);
      setError(error.message);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Navbar />
      <Grid container spacing={2} alignItems="center" justifyContent="center" style={{ minHeight: '100vh' }}>
        <Grid item xs={12} sm={6}>
          <h2>{t('auth.signupHeading')}</h2>
          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          {successMessage ? (
            <Typography color="primary" variant="body1">
              {successMessage} <MuiLink component={Link} to="/">{t('auth.homeLink')}</MuiLink>
            </Typography>
          ) : (
            <>
              <TextField
                label={t('auth.emailLabel')}
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                margin="normal"
              />
              <TextField
                label={t('auth.passwordLabel')}
                variant="outlined"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                margin="normal"
              />
              <TextField
                label={t('auth.confirmPasswordLabel')}
                variant="outlined"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                margin="normal"
              />
              <Button onClick={signup} variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
                {t('auth.signupButton')}
              </Button>
              <Button
                onClick={signInWithGoogle}
                variant="outlined"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
              >
                {t('auth.signupGoogleButton')}
              </Button>
              <MuiLink component={Link} to="/login" variant="body2" display="block" sx={{ mt: 2 }}>
                {t('auth.alreadyRegisteredLink')}
              </MuiLink>
            </>
          )}
        </Grid>
      </Grid>
      <Footer />
    </ThemeProvider>
  );
}

export default Signup;
